// localStorage cache for the FKE narrative summaries (OKV-234 / FkeNarrativeSummary).
//
// Kept in its own dependency-free module so the authentication plugin can clear it at login/logout
// without creating an import cycle (fkeSummary.js -> fetcher.js -> authentication.js).
//
// Entries are keyed by the caller's cacheKey + language and carry a timestamp: a stale entry (older
// than the TTL) is treated as a miss and dropped, so long-lived sessions still refresh. All access is
// wrapped in try/catch because localStorage can throw or be unavailable (private mode, blocked
// storage, quota).
const FKE_SUMMARY_CACHE_PREFIX = 'okv.fkeSummary.'
const FKE_SUMMARY_CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24h

function cacheStorageKey(cacheKey, language) {
  return `${FKE_SUMMARY_CACHE_PREFIX}${cacheKey}.${language}`
}

export function readFkeSummaryCache(cacheKey, language) {
  if (!cacheKey) return null
  try {
    const raw = localStorage.getItem(cacheStorageKey(cacheKey, language))
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (!entry || typeof entry.text !== 'string' || typeof entry.ts !== 'number') return null
    if (Date.now() - entry.ts > FKE_SUMMARY_CACHE_TTL_MS) {
      try {
        localStorage.removeItem(cacheStorageKey(cacheKey, language))
      } catch (e) {
        /* ignore */
      }
      return null
    }
    return entry.text
  } catch (e) {
    return null
  }
}

export function writeFkeSummaryCache(cacheKey, language, text) {
  if (!cacheKey || !text) return
  try {
    localStorage.setItem(cacheStorageKey(cacheKey, language), JSON.stringify({ text, ts: Date.now() }))
  } catch (e) {
    /* storage unavailable/full — degrade to non-persistent */
  }
}

// Remove every cached summary. Called by the auth plugin at login and logout so a session always
// starts from a clean cache.
export function clearFkeSummaryCache() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.indexOf(FKE_SUMMARY_CACHE_PREFIX) === 0)
      .forEach((key) => localStorage.removeItem(key))
  } catch (e) {
    /* storage unavailable */
  }
}
